import { useMemo } from 'react';

import { TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';
import { useZCalendarWebApiHandler_getTourConfigAndAvailability } from '@zg-rentals/ts-z-calendar-client-react-query';

import { ZCALENDAR_TOUR_CONFIG_QUERY_TAG } from '../constants/queryTags';
import { convertDateToLocalDate } from '../utils/formatTime';

type Options = {
  enabled?: boolean;
};

/**
 * Reads a listing's tour config: whether touring is set up at all, and whether it books tours
 * instantly rather than requiring landlord confirmation. The endpoint returns config alongside
 * availability, so the range is collapsed to a single day — only the config is read.
 *
 * `hasTourConfig` is `undefined` until the answer is known, so callers can tell "no touring" apart
 * from "not loaded yet" and avoid hiding UI on a slow or failed request.
 */
const useListingTourConfig = (listingAlias: string, { enabled = true }: Options = {}) => {
  const today = useMemo(() => new Date(), []);

  const { data, isError, isLoading, isPending } = useZCalendarWebApiHandler_getTourConfigAndAvailability(
    {
      listingAlias,
      fromDate: convertDateToLocalDate(today),
      toDate: convertDateToLocalDate(today),
    },
    {
      enabled: enabled && Boolean(listingAlias),
      meta: {
        tags: [ZCALENDAR_TOUR_CONFIG_QUERY_TAG],
        context: {
          api: 'getTourConfigAndAvailability',
          details: 'useListingTourConfig - Error fetching listing tour config',
        },
      },
    },
  );

  const hasTourConfig = data ? Boolean(data.tourConfig) : undefined;

  const isInstantBook =
    data?.tourConfig?.tourSchedulingTypes.schedulingTypes[0]?.confirmationType === TourConfirmationType.INSTANT;

  return { hasTourConfig, isError, isInstantBook, isLoading, isPending };
};

export default useListingTourConfig;
